import { useFormContext } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { cn } from "@/lib/utils";
import {
  CART_LINE_TEMPLATE_VARIABLES,
  DEFAULT_WHATSAPP_CART_MESSAGE_TEMPLATE,
  DEFAULT_WHATSAPP_PRODUCT_MESSAGE_TEMPLATE,
  WHATSAPP_TEMPLATE_VARIABLES,
} from "@/lib/whatsapp";
import type { StoreSettingsFormValues } from "./StoreSettingsForm";

const textareaClassName = cn(
  "flex w-full border border-primary-200 dark:border-primary-800 bg-white dark:bg-primary-950 px-4 py-3 text-sm font-mono font-light text-primary-950 dark:text-white transition-colors duration-300 placeholder:text-primary-400 focus-visible:outline-none focus-visible:border-primary-400"
);

function VariableHelp({ scope }: { scope: "product" | "cart" }) {
  const vars = WHATSAPP_TEMPLATE_VARIABLES.filter((v) =>
    (v.scopes as readonly string[]).includes(scope)
  );

  return (
    <div className="space-y-2 text-xs font-light text-primary-500 dark:text-primary-500">
      <p>
        Variáveis:{" "}
        {vars.map((v, i) => (
          <span key={v.key}>
            {i > 0 && ", "}
            <code className="text-primary-700 dark:text-primary-300">{`{{${v.key}}}`}</code>
            {" "}({v.label})
          </span>
        ))}
        . Use *texto* para negrito e _texto_ para itálico no WhatsApp.
      </p>
      {scope === "cart" && (
        <p>
          Itens do carrinho (repita por produto):{" "}
          <code className="text-primary-700 dark:text-primary-300">
            {`{{#products}} ... {{/products}}`}
          </code>
          {" "}com{" "}
          {CART_LINE_TEMPLATE_VARIABLES.map((v, i) => (
            <span key={v.key}>
              {i > 0 && ", "}
              <code className="text-primary-700 dark:text-primary-300">{`{{${v.key}}}`}</code>
            </span>
          ))}
          .
        </p>
      )}
    </div>
  );
}

export function WhatsAppMessageTemplatesSection() {
  const form = useFormContext<StoreSettingsFormValues>();

  return (
    <div className="space-y-6 pt-6 border-t border-primary-200 dark:border-primary-800">
      <div>
        <h3 className="text-lg font-light tracking-tight text-primary-900 dark:text-white">
          Mensagens do WhatsApp
        </h3>
        <p className="text-sm font-light text-primary-600 dark:text-primary-400 mt-1">
          Personalize o texto enviado quando o cliente clica em Falar no WhatsApp. Toda loja usa
          um modelo padrão até você personalizar.
        </p>
      </div>

      <FormField
        control={form.control}
        name="whatsappProductMessageTemplate"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Mensagem — produto único</FormLabel>
            <FormControl>
              <textarea
                {...field}
                value={field.value ?? ""}
                rows={10}
                className={textareaClassName}
                placeholder={DEFAULT_WHATSAPP_PRODUCT_MESSAGE_TEMPLATE}
              />
            </FormControl>
            <VariableHelp scope="product" />
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  form.setValue(
                    "whatsappProductMessageTemplate",
                    DEFAULT_WHATSAPP_PRODUCT_MESSAGE_TEMPLATE,
                    { shouldDirty: true }
                  )
                }
              >
                Restaurar padrão
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() =>
                  form.setValue("whatsappProductMessageTemplate", DEFAULT_WHATSAPP_PRODUCT_MESSAGE_TEMPLATE, {
                    shouldDirty: true,
                  })
                }
              >
                Usar padrão
              </Button>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="whatsappCartMessageTemplate"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Mensagem — carrinho</FormLabel>
            <FormControl>
              <textarea
                {...field}
                value={field.value ?? ""}
                rows={12}
                className={textareaClassName}
                placeholder={DEFAULT_WHATSAPP_CART_MESSAGE_TEMPLATE}
              />
            </FormControl>
            <VariableHelp scope="cart" />
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  form.setValue(
                    "whatsappCartMessageTemplate",
                    DEFAULT_WHATSAPP_CART_MESSAGE_TEMPLATE,
                    { shouldDirty: true }
                  )
                }
              >
                Restaurar padrão
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() =>
                  form.setValue("whatsappCartMessageTemplate", DEFAULT_WHATSAPP_CART_MESSAGE_TEMPLATE, {
                    shouldDirty: true,
                  })
                }
              >
                Usar padrão
              </Button>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
